'use client'

import { createContext, useContext, useState, useCallback } from 'react'

export interface BrandingConfig {
  accentColor: string
  accentMuted: string
  bgPrimary: string
  bgSurface: string
  bgSecondary: string
  bgElevated: string
  bgInput: string
  borderColor: string
  textColor: string
  textMuted: string
  textSubtle: string
  logoUrl?: string | null
}

interface BrandingContextValue {
  branding: BrandingConfig
  updateBranding: (partial: Partial<BrandingConfig>) => Promise<void>
}

const BrandingContext = createContext<BrandingContextValue | null>(null)

export function useBranding() {
  const ctx = useContext(BrandingContext)
  if (!ctx) throw new Error('useBranding must be used inside BrandingProvider')
  return ctx
}

interface BrandingProviderProps {
  branding: BrandingConfig
  children: React.ReactNode
}

export default function BrandingProvider({ branding: initial, children }: BrandingProviderProps) {
  const [branding, setBranding] = useState<BrandingConfig>(initial)

  const updateBranding = useCallback(async (partial: Partial<BrandingConfig>) => {
    setBranding(prev => ({ ...prev, ...partial }))
    await fetch('/api/settings/branding', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    })
  }, [])

  return (
    <BrandingContext.Provider value={{ branding, updateBranding }}>
      {children}
    </BrandingContext.Provider>
  )
}
